<template>
  <section
    :class="['nb-banner-media', variant]"
    :style="variant === 'primary' ? backgroundStyle : styles"
    :aria-labelledby="`banner-title-${uniqueId}`"
  >
    <div
      :class="['nb-banner-media__heading', variant]"
      :style="variant === 'primary' ? styles : ''"
    >
      <p :style="titleStyles" :class="['nb-banner-media__title', titleClasses]" :id="`banner-title-${uniqueId}`">
        <slot name="title" />
      </p>
      <p
        :style="subTitleStyles"
        :class="['nb-banner-media__subtitle', subtitleClasses]"
      >
        <slot name="subtitle" />
      </p>
      <div v-if="variant === 'secondary'">
        <a
          :style="buttonStyles"
          :class="['nb-banner-media__button', variant]"
          href=""
          aria-label="play"
          v-openmodal="{
            type,
            poster: imageDesktop,
            preferr,
            medias,
          }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="icon icon-tabler icons-tabler-filled icon-tabler-player-play"
            aria-hidden="true"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path
              d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z"
            />
          </svg>
        </a>
      </div>
    </div>

    <div
      v-if="variant === 'primary'"
      :class="['nb-banner-media__buttonContainer']"
    >
      <a
        :class="['nb-banner-media__button', variant]"
        :style="buttonStyles"
        href=""
        aria-label="play"
        v-openmodal="{
          type,
          poster: imageDesktop,
          preferr,
          medias,
        }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="icon icon-tabler icons-tabler-filled icon-tabler-player-play"
          aria-hidden="true"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path
            d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z"
          />
        </svg>
      </a>
    </div>

    <div
      v-if="variant === 'secondary'"
      :class="['nb-banner-media__imageContainer']"
    >
      <picture>
        <source media="(max-width: 480px)" :srcset="imageMobile" />
        <source
          media="(min-width: 481px) and (max-width: 1200px)"
          :srcset="imageTablet"
        />
        <img
          :src="imageDesktop"
          :alt="alt"
          :class="['nb-banner-media__image', variant]"
          :style="{
            border: hasBorder ? `solid 0.3125rem ${borderColor}` : 'none',
          }"
        />
      </picture>
    </div>
  </section>
</template>

<script>

import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'nb-banner-media',
  props: {
    variant: {
      type: String,
      default: 'primary',
    },
    bgColor: {
      type: String,
    },
    titleColor: {
      type: String,
    },
    titleSize: {
      type: String,
      description: '(small / medium / large / extra-large / larger)',
    },
    titleWeight: {
      type: String,
    },
    subtitleColor: {
      type: String,
    },
    subtitleSize: {
      type: String,
      description: '(small / medium / large / extra-large / larger)',
    },
    subtitleWeight: {
      type: String,
    },
    hasGradient: {
      type: Boolean,
      default: false,
    },
    gradientStartColor: {
      type: String,
    },
    gradientEndColor: {
      type: String,
    },
    opacity: {
      type: String,
    },
    type: {
      type: String,
      default: 'video',
    },
    preferr: {
      type: String,
      default: 'youtube',
    },
    medias: {
      type: Object,
      default: null,
    },
    imageMobile: {
      type: String,
    },
    imageTablet: {
      type: String,
    },
    imageDesktop: {
      type: String,
    },
    alt: {
      type: String,
    },
    buttonBgColor: {
      type: String,
    },
    buttonIconColor: {
      type: String,
    },
    hasBorder: {
      type: Boolean,
    },
    borderColor: {
      type: String,
    },
  },
  data() {
    return {
      screenWidth: window.innerWidth,
      uniqueId: uuidv4()
    }
  },
  computed: {
    classes() {
      return this.variant ? `nb-banner-media--${this.variant}` : ''
    },
    styles() {
      const startColor = this.gradientStartColor || '#44564C'
      const endColor = this.gradientEndColor || '#213027'

      return {
        backgroundColor: this.bgColor,
        background: this.hasGradient
          ? `linear-gradient(180deg, ${startColor} 0%, ${endColor} 100%)`
          : this.bgColor,
        ...(this.hasGradient
          ? {}
          : { opacity: this.opacity ? `${this.opacity}%` : '85%' }),
      }
    },
    titleStyles() {
      return {
        color: this.titleColor,
        fontWeight: this.titleWeight,
      }
    },
    subTitleStyles() {
      return {
        color: this.subtitleColor,
        fontWeight: this.subtitleWeight,
      }
    },
    titleClasses() {
      const size = this.titleSize || 'larger'
      return `nb-banner-media__title--${size}`
    },
    subtitleClasses() {
      const size = this.subtitleSize || 'larger'
      return `nb-banner-media__subtitle--${size}`
    },
    buttonStyles() {
      return {
        backgroundColor: this.buttonBgColor ? this.buttonBgColor : '#9DDDA9',
        color: this.buttonIconColor ? this.buttonIconColor : '#FFFFFF',
      }
    },
    backgroundStyle() {
      let imageUrl = this.imageDesktop

      if (this.screenWidth < 768) {
        imageUrl = this.imageMobile
      } else if (this.screenWidth < 1200) {
        imageUrl = this.imageTablet
      }

      return {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth
    },
  },
}
</script>

<style lang="scss" scoped>
.nb-banner-media {
  background-color: #9ddda9;
  width: -webkit-fill-available;

  &.primary {
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-height: 50rem;

    @media screen and (min-width: 1200px) {
      flex-direction: row;
      align-items: center;
      height: unset;
      max-height: unset;
    }
  }

  &.secondary {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 2rem;

    @media screen and (min-width: 768px) {
      padding: 4rem;
    }

    @media screen and (min-width: 1200px) {
      display: flex;
      align-items: center;
      flex-direction: row-reverse;
      justify-content: center;
      gap: 5%;
      padding: 4rem 8rem;
    }
  }

  &__heading {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    color: #213027;

    @media screen and (min-width: 1200px) {
      gap: 2.5rem;
    }

    &.primary {
      padding: 2rem;

      @media screen and (min-width: 768px) {
        padding: 4rem;
      }

      @media screen and (min-width: 1200px) {
        align-items: unset;
        max-width: 56.25rem;
      }

      @media screen and (min-width: 2560px) {
        max-width: 80rem;
      }
    }

    &.secondary {
      @media screen and (min-width: 1200px) {
        gap: 1rem;
      }
    }
  }

  &__title {
    font-weight: 300;
    line-height: 100%;

    &--small {
      font-size: 1.25rem;

      @media screen and (min-width: 768px) {
        font-size: 1.5rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 2rem;
      }
    }

    &--medium {
      font-size: 1.5rem;

      @media screen and (min-width: 768px) {
        font-size: 2rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 3rem;
      }
    }

    &--large {
      font-size: 1.75rem;

      @media screen and (min-width: 768px) {
        font-size: 2.5rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 4.5rem;
      }
    }

    &--extra-large {
      font-size: 2rem;

      @media screen and (min-width: 768px) {
        font-size: 3rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 5.25rem;
      }
    }

    &--larger {
      font-size: 2.25rem;

      @media screen and (min-width: 768px) {
        font-size: 3.5rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 6.25rem;
      }
    }
  }

  &__subtitle {
    font-weight: 400;

    &--small {
      font-size: 1rem;

      @media screen and (min-width: 768px) {
        font-size: 1.25rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 1.5rem;
      }
    }

    &--medium {
      font-size: 1.25rem;

      @media screen and (min-width: 768px) {
        font-size: 1.5rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 2rem;
      }
    }

    &--large {
      font-size: 1.5rem;

      @media screen and (min-width: 768px) {
        font-size: 1.75rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 2.5rem;
      }
    }

    &--extra-large {
      font-size: 1.75rem;

      @media screen and (min-width: 768px) {
        font-size: 2rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 2.75rem;
      }
    }

    &--larger {
      font-size: 2rem;

      @media screen and (min-width: 768px) {
        font-size: 2.25rem;
      }

      @media screen and (min-width: 1200px) {
        font-size: 3rem;
      }
    }
  }

  &__buttonContainer {
    display: flex;
    justify-content: center;

    @media screen and (min-width: 1200px) {
      display: unset;
    }
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    width: 3.75rem;
    height: 3.75rem;

    @media screen and (min-width: 1200px) {
      width: 5rem;
      height: 5rem;
    }

    &.primary {
      position: relative;
      bottom: 1.25rem;

      @media screen and (min-width: 1200px) {
        margin-inline: 2rem;
        bottom: unset;
        right: 2.5rem;
        margin-inline: unset;
      }
    }

    &.secondary {
      @media screen and (min-width: 1200px) {
        margin-top: 1.25rem;
      }
    }
  }

  &__imageContainer {
    height: 100%;
  }

  &__image {
    &.primary {
      display: flex;
      object-fit: cover;
      width: 100%;
      height: 100%;
    }

    &.secondary {
      width: 100%;
      box-shadow: 0.25rem 0.25rem 0.9375rem 0.625rem rgba(0, 0, 0, 0.4);
      border-radius: 1.25rem;
      object-fit: cover;

      @media screen and (min-width: 1200px) {
        height: 27.25rem;
      }

      @media screen and (min-width: 1440px) {
        width: 47.3125rem;
      }
    }
  }
}
</style>
